import { checkInvitationCodeIsValid, getInvitationCode } from "@/api/process.api";
import { CardError } from "@/components/CardError";
import { CONFIG } from "@/config";
// import { SelectLang } from "@/components/SelectLang";
import { auth, db } from "@/config/firebase";
import { ErrorType } from "@/interfaces";
import { InvitationCodeModel } from "@/interfaces/User.interface";
import { ErrorHandling } from "@/utils/ErrorHandling";
import { T, useTranslate } from "@tolgee/react";
import { Button, Card, Form, Input, message, Typography } from "antd";
import { createUserWithEmailAndPassword, UserCredential } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { getDeviceInfo } from '@/utils/DeviceUtils.ts';

const CardStyled = styled(Card)`
  width: 395px;
  background-color: #f8f8f8;
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  padding: 40px 40px 0px 40px;
  @media screen and (max-width: 769px) {
    width: 337px;
    padding: 40px 18px 53px 18px;
  }
`;

type RegisterFormType = {
  name: string;
  email: string;
  password: string;
  invitationCode: string;
};
const RegisterPage: React.FC = () => {
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const [invitationCodeData, setInvitationCodeData] = useState<InvitationCodeModel | null>(null);
  const [error, setError] = useState<ErrorType | null>(null);

  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const validateInvitationWithAccessCode = async (values: RegisterFormType) => {
    if (values.invitationCode !== invitationCodeData?.code) {
      throw new Error(t('invitation-code-invalid'));
    }
    if (values.email !== invitationCodeData?.email) {
      throw new Error(t('invitation-code-email-not-match'));
    }
    if (invitationCodeData?.status === 'used') {
      throw new Error(t('invitation-code-used'));
    }
  };

  const validateInvitationWithoutAccessCode = async (values: RegisterFormType) => {
    const isValid = await checkInvitationCodeIsValid(values.invitationCode, values.email);
    if (!isValid) {
      throw new Error(t('invitation-code-invalid'));
    }
  };

  const createUserData = (userCredential: UserCredential, values: RegisterFormType) => {
    const initialDevice = getDeviceInfo();
    return {
      userId: userCredential.user.uid,
      name: values.name,
      email: values.email,
      trialEndDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days
      appVersion: CONFIG.appVersion || '1.0.0',
      stripeInfo: {
        status: 'inactive',
        stripeCustomerId: null,
      },
      inAppPaymentInfo: {
        subscriptionId: null,
        status: 'inactive',
        gateway: 'google',
        purchaseToken: null,
        iapSubscriptionStatus: 'SUBSCRIPTION_INACTIVE',
      },
      devices: [initialDevice],
      invitationCode: values.invitationCode,
    }
  };

  const handleSubmit = async (values: RegisterFormType) => {
    setLoading(true);
    try {
      // Validate invitation code
      if (searchParams.get('accessCode')) {
        await validateInvitationWithAccessCode(values);
      } else {
        await validateInvitationWithoutAccessCode(values);
      }

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, values.email, values.password);

      // Create and save user data
      const userData = createUserData(userCredential, values);
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, userData);

      message.success(t('registed_complete'));
      navigate('/welcome');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Sentry.captureException(error);
      setError({
        message: t(ErrorHandling(error)),
      });

      // Cleanup if user creation partially succeeded
      // if (auth.currentUser) {
      //   await auth.currentUser.delete();
      //   await auth.signOut();
      // }
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitationCode = async () => {
    const response = await getInvitationCode(searchParams.get('accessCode') as string);
    if (response) {
      setInvitationCodeData(response);
      form.setFieldsValue({
        invitationCode: response.code,
        email: response.email,
      });
    }
  };

  useEffect(() => {
    if (searchParams.get('accessCode')) {
      fetchInvitationCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, form]);

  return (
    <div className={'div-responsive'}>
      <Form form={form} onFinish={handleSubmit}>
        <CardStyled size={'small'}>
          <div className={'flex justify-center flex-col items-center'}>
            <img alt={'logo'} src="/logo.svg" className="absolute -top-10" />
            <Typography.Text className={'text-center text-black text-xl font-bold uppercase w-full'}>
              PURITY
            </Typography.Text>
            <Typography.Text className={'text-center text-black text-sm font-bold w-full'}>
              {t('user-dashboard')}
            </Typography.Text>
          </div>
          <Form.Item
            className="mt-3"
            name={'name'}
            rules={[
              {
                required: true,
                message: t('input_name'),
              },
            ]}
          >
            <Input placeholder={t('name')} />
          </Form.Item>
          <Form.Item
            name={'email'}
            rules={[
              {
                required: true,
                message: t('input_email'),
              },
            ]}
          >
            <Input placeholder={t('email-address')} disabled={Boolean(invitationCodeData?.email)} />
          </Form.Item>
          <Form.Item
            name={'password'}
            rules={[
              {
                required: true,
                message: t('input_password'),
              },
            ]}
          >
            <Input.Password placeholder={t('password')} className={'px-3 py-0 rounded-sm'} visibilityToggle={false} />
          </Form.Item>
          <Form.Item
            name={'invitationCode'}
            className="mb-2"
            rules={[
              {
                required: true,
                message: t('input_invitation_code'),
              },
            ]}
          >
            <Input placeholder={t('invitation-code')} />
          </Form.Item>
          <div className="w-full flex justify-center items-center">
            <span className={'text-center text-black text-tiny w-full'}>
              {t('terms-service-desc')}{' '}
              <a
                href={
                  'https://docs.google.com/document/d/10Aw61-5J96-X2fw-moj9VIDhWVINvGkpVN-n66ctcHg/edit?usp=sharing'
                }
                className={'font-bold'}
              >
                <b>{t('terms-of-use')}</b>
              </a>
            </span>
          </div>
          <Button className={'w-full bg-primary text-white mb-6 mt-2 font-bold'} htmlType={'submit'} loading={loading}>
            <T keyName={'create-account-button'} />
          </Button>
          <div className={'flex justify-center items-center w-full'}>
            <Link to="https://user.puritybrowser.com/login">
              <Button type="link" className={'font-bold'}>
                {t('log-in')}
              </Button>
            </Link>
          </div>
          {/* <div className="flex justify-center items-center w-full mt-3">
              <SelectLang />
            </div> */}
        </CardStyled>
      </Form>
      {error && <CardError message={error.message} onOk={() => setError(null)} />}
    </div>
  );
};

export default RegisterPage;
